import { Link } from 'react-router-dom';
import { StarIcon } from 'lucide-react';
import { REVIEWS_CLAUSE } from '../../data/legal';
import { founder, salon } from '../../data/content';
import { rating } from '../../core/format/currency';
import { plural } from '../../core/format/text';
import { shopLabels } from '../../data/shopLabels';
import type { Product, Review } from '../../core/types/catalog';

/** Ervaringen: het gemiddelde links, geschreven reviews rechts. */
export function ProductReviews({
  product,
  reviews
}: {
  product: Product;
  reviews: Review[];
}) {
  return <section id="reviews" className="scroll-mt-28 border-t border-line bg-sand py-16 lg:py-20" aria-labelledby="reviews-title" data-id="element-8145" data-mp="2hsx2s5j48aao">
      <div className="mx-auto grid max-w-content gap-10 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-8146" data-mp="28rfs52oh90td">
        <div className="lg:col-span-4" data-id="element-8147" data-mp="12bdisluyrk1w">
          <h2 id="reviews-title" className={product.reviews > 0 ? 'sr-only' : 'type-sub text-ink'} data-id="element-8148" data-mp="12cewyz7a0mnu">
            Ervaringen
          </h2>
          {product.reviews > 0 && <>
              <p className="type-figure text-ink" aria-hidden="true" data-id="element-8149" data-mp="16b4mfm27ogd7">
                {rating(product.rating)}
              </p>
              <p className="mt-2 text-[15px] text-muted" data-id="element-8150" data-mp="1b8d6js6x6uh3">
                <span className="sr-only" data-id="element-8151" data-mp="xa6guzc4k0si">{rating(product.rating)} van 5, </span>
                gemiddeld uit {plural(product.reviews, 'beoordeling', 'beoordelingen')}
              </p>
            </>}
        </div>
        <ul className="grid gap-8 sm:grid-cols-2 lg:col-span-8" data-id="element-8152" data-mp="nisdggh1ygu3">
          {reviews.map(r => <li key={`${r.name}-${r.body.slice(0, 24)}`} className="border-t border-ink/15 pt-5" data-id="element-8153" data-mp="sb25q35n3szj">
              {/* Altijd vijf sterren, de lege gedempt: zo lees je "4 van 5" in één oogopslag. */}
              <p className="flex gap-0.5" data-id="element-8154" data-mp="ila81028ujqu">
                <span className="sr-only" data-id="element-8155" data-mp="4kpr69fw2xzz">{r.rating} van 5</span>
                {Array.from({
              length: 5
            }).map((_, i) => <StarIcon key={i} className={`h-3.5 w-3.5 fill-current ${i < r.rating ? 'text-clay' : 'text-ink/15'}`} strokeWidth={0} aria-hidden="true" data-id="element-8156" data-mp="9k2d8ryxs11i" data-mpi={i} />)}
              </p>
              <blockquote className="mt-3 type-name text-ink" data-id="element-8157" data-mp="26hq5wpzoyueo">“{r.body}”</blockquote>
              <p className="mt-3 text-[14px] text-muted" data-id="element-8158" data-mp="22geqteeg1rkq">
                {r.name} · {r.context}
                {r.verified && ' · geverifieerd'}
              </p>
            </li>)}
          {reviews.length === 0 && <li className="text-[15px] leading-relaxed text-muted sm:col-span-2" data-id="element-8159" data-mp="2u6sxoqo1i3mw">
              Nog geen geschreven review voor dit product. Gebruik je het al?{' '}
              <a href={salon.whatsapp} target="_blank" rel="noreferrer" className="text-ink underline underline-offset-4" data-id="element-8160" data-mp="2l6w4cbqpggli">
                Laat {founder.firstName} weten wat je ervan vindt
                <span className="sr-only" data-id="element-8161" data-mp="19rzdcwaynhbi">{shopLabels.viaWhatsApp}</span>
              </a>
              .
            </li>}
        </ul>
        {/* Omnibus: zeggen hoe reviews gecontroleerd worden, met de clausule erbij. */}
        {reviews.length > 0 && <p className="text-[13px] leading-relaxed text-muted lg:col-span-8 lg:col-start-5" data-id="element-8162" data-mp="uqsjrm95fgpg">
            Alleen wie het product kocht of de behandeling had, kan een review plaatsen. {founder.firstName} plaatst positieve én kritische reviews, ongewijzigd.{' '}
            <Link to={REVIEWS_CLAUSE} className="text-ink underline underline-offset-4" data-id="element-8163" data-mp="19e3szhhuyome">
              Zo controleren we reviews
            </Link>
          </p>}
      </div>
    </section>;
}